import React from "react";
import { color, radius, space, text, weight, tracking } from "../tokens";

/**
 * <Chip> — status pill.
 *
 *   tone   : "neutral" | "brand" | "success" | "warning" | "danger" | "info"
 *   shape  : "pill"   default — fully rounded
 *            "square" — 6px radius, for compact table cells
 *   size   : "sm" (20px tall) default | "md" (24px tall)
 *   dot    : boolean — leading colored dot
 *   pulse  : boolean — pulsing dot (for live-status chips)
 *
 * Never emit more than one brand chip per screen.
 */
const TONE_STYLES = {
  neutral: { bg: color.bg.subtle,     fg: color.text.secondary, dot: color.text.tertiary },
  brand:   { bg: color.brand.subtle,  fg: color.brand.text,     dot: color.brand.solid },
  success: { bg: color.success.bg,    fg: color.success.text,   dot: color.success.solid },
  warning: { bg: color.warning.bg,    fg: color.warning.text,   dot: color.warning.solid },
  danger:  { bg: color.danger.bg,     fg: color.danger.text,    dot: color.danger.solid },
  info:    { bg: color.info.bg,       fg: color.info.text,      dot: color.info.solid },
};

export function Chip({
  children,
  tone = "neutral",
  shape = "pill",
  size = "sm",
  dot = false,
  pulse = false,
  style,
}) {
  const s = TONE_STYLES[tone] || TONE_STYLES.neutral;
  const height = size === "md" ? 24 : 20;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: space[2],
        height,
        padding: `0 ${space[2]}px`,
        borderRadius: shape === "pill" ? radius.pill : radius.md,
        background: s.bg,
        color: s.fg,
        fontSize: text.xs,
        fontWeight: weight.semibold,
        letterSpacing: tracking.wide,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: radius.pill,
            background: s.dot,
            display: "inline-block",
            animation: pulse ? "idz-pulse 1.4s ease-in-out infinite" : "none",
          }}
        />
      )}
      {children}
    </span>
  );
}
